import {
	Autocomplete, DatePicker, DefaultButton, Space, TableItem, StatusLabel,
} from '@Atoms';
import { formatCurrency, formatPercentage } from '@Helpers';
import { PaperContainer } from '@Molecules';
import { Summarize } from '@mui/icons-material';
import { Grid, useTheme } from '@mui/material';
import { DataTable, TableColumn } from '@Organisms';
import { ICouponReport, ICouponReportReq } from '@Types';
import { INameAndId } from 'data/models/misc.model';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface CouponReportPage{
	couponReport?: ICouponReport[];
	categories: INameAndId[];
	couponRequest: ICouponReportReq;
	onChange: (key: string, value: Date | INameAndId[]) => void;
	onFilterClick: () => void;
	loading: boolean;
	onExportClick: ()=>void
}

const CouponsReportPage: React.FC<CouponReportPage> = (props) => {
	const {
		couponReport,
		categories,
		couponRequest,
		onChange,
		onFilterClick,
		loading,
		onExportClick,
	} = props;
	const theme = useTheme();
	const { t } = useTranslation('translations');
	return (
		<div>
			<Grid sx={{ padding: theme.spacing(3) }} container spacing={3}>
				<Grid item xs={12} md={12}>
					<PaperContainer>
						<Grid container spacing={2}>
							<Grid item xs={12} md={3}>
								<DatePicker
									label={t('startDate')}
									value={couponRequest.initialDate}
									disabled={loading}
									onChange={(value) => onChange('initialDate', value)}
								/>
							</Grid>
							<Grid item xs={12} md={3}>
								<DatePicker
									label={t('endDate')}
									value={couponRequest.finalDate}
									disabled={loading}
									onChange={(value) => onChange('finalDate', value)}
								/>
							</Grid>
							<Grid item xs={12} md={3}>
								<Autocomplete
									label={t('categories')}
									placeholder=""
									multiple
									disabled={loading}
									options={categories}
									defaultValue={couponRequest.categories}
									isOptionEqualToValue={(option, value) => option.id === value.id}
									renderOption={(inputprops, item) => (
										<li
											{...inputprops}
											key={item.id}
										>
											{item.name}
										</li>
									)}
									getOptionLabel={(item) => item.name}
									onChange={(_, value) => onChange('categories', value)}
								/>
							</Grid>
							<Grid item xs={12} md={3}>
								<DefaultButton
									title={t('filter')}
									onClick={onFilterClick}
									loading={loading}
								/>
							</Grid>
						</Grid>
					</PaperContainer>
				</Grid>
			</Grid>
			<Grid
				sx={{
					pr: theme.spacing(3),
					pl: theme.spacing(3),
					pb: theme.spacing(3),
				}}
				container
				spacing={3}
			>
				<Grid item xs={12} md={12}>
					<PaperContainer>
						<DefaultButton title={t('export')} startIcon={<Summarize />} onClick={onExportClick} />
						<Space height={2} />
						<DataTable data={couponReport || []} search={false}>
							<TableColumn headerText="#" render={(item) => <TableItem title={`${item.couponId}`} />} />
							<TableColumn headerText={t('title')} render={(item) => <TableItem title={item.title} />} />
							<TableColumn align="center" headerText={t('limit')} render={(item) => item.maxUsage} />
							<TableColumn align="center" headerText={t('cost')} render={(item) => item.cost} />
							<TableColumn align="center" headerText={t('bonus')} render={(item) => (item.isPercentage ? formatPercentage(item.amount) : formatCurrency(item.amount))} />
							<TableColumn align="center" headerText={t('issued')} render={(item) => item.released} />
							<TableColumn align="center" headerOptions={{ align: 'center' }} headerText={t('quantity_users_covered')} render={(item) => item.distinctReleased} />
							<TableColumn align="center" headerText={t('views')} render={(item) => item.views} />
							<TableColumn align="center" headerText={t('requested')} render={(item) => item.requested} />
							<TableColumn align="center" headerText={t('used')} render={(item) => item.used} />
							<TableColumn align="center" headerText={t('rejected')} render={(item) => item.rejected} />
							<TableColumn headerText={t('category')} render={(item) => item.category} />
							<TableColumn headerText={t('status')} render={(item) => <StatusLabel status={item.status} />} />
						</DataTable>
					</PaperContainer>
				</Grid>
			</Grid>
		</div>
	);
};

export default CouponsReportPage;
